// Task #3839 — DTO risposta condivisi per il modulo HR (catalogo tipologie,
// routing destinatari, gerarchie, conflitti turni↔assenze, copertura store,
// timbrature). SoT unica BE↔FE: il backend (hr.service e servizi derivati) li
// usa come tipi di ritorno, il FE come generic di `apiClient.get<T>`.
// Tutti gli endpoint HR usano l'envelope `{ success, data }`: qui modelliamo
// SOLO il payload `data`. Convenzioni wire come calendar.ts:
//  - timestamptz pg → z.union([z.string(), z.date()]) (ISO string sul wire);
//  - numeric/SUM senza ::int → z.union([z.string(), z.number()]) (pg numeric =
//    stringa, memoria pg-numeric-string-fe-tofixed); COUNT(...)::int → z.number();
//  - date pg → string 'YYYY-MM-DD' sul wire ma Date lato service pre-serialize
//    → z.union([z.string(), z.date()]);
//  - righe da `SELECT x.*` → colonne note + .passthrough().

import { z } from 'zod';
import { permissionGrantSchema } from './security';

/** pg numeric sul wire node-postgres = stringa; number se già castato. */
const wireNumeric = z.union([z.string(), z.number()]);
/** timestamptz: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);
/** colonna `date` pg: 'YYYY-MM-DD' sul wire, Date lato BE. */
const wireDate = z.union([z.string(), z.date()]);
/** Date civili dei percorsi turni/presenze: proiettate come testo dal DB. */
export const hrCalendarDateSchema = z.string().date();

export const hrShiftAssignmentInputSchema = z.object({
  user_id: z.string().min(1),
  shift_template_id: z.string().min(1),
  store_id: z.string().min(1),
  assignment_date: hrCalendarDateSchema,
  notes: z.string().nullable().optional(),
});
export type HrShiftAssignmentInput = z.infer<typeof hrShiftAssignmentInputSchema>;
export const hrShiftAssignmentUpdateSchema = hrShiftAssignmentInputSchema
  .pick({ shift_template_id: true, store_id: true, notes: true }).partial();
export type HrShiftAssignmentUpdate = z.infer<typeof hrShiftAssignmentUpdateSchema>;
export const hrShiftAssignmentBulkInputSchema = z.object({
  assignments: z.array(hrShiftAssignmentInputSchema).min(1).max(2000),
  override: z.boolean().default(false),
});
export type HrShiftAssignmentBulkInput = z.infer<typeof hrShiftAssignmentBulkInputSchema>;
/** Gli array legacy NON autorizzano la sovrascrittura. */
export const hrShiftAssignmentCreateSchema = z.union([
  hrShiftAssignmentInputSchema,
  z.array(hrShiftAssignmentInputSchema).min(1).max(2000),
  hrShiftAssignmentBulkInputSchema,
]);
export const hrCopyWeekInputSchema = z.object({
  source_week_start: hrCalendarDateSchema,
  target_week_start: hrCalendarDateSchema.optional(),
  target_week_starts: z.array(hrCalendarDateSchema).max(12).optional(),
  store_ids: z.array(z.string().min(1)).optional(),
  override: z.boolean().optional(),
});
export type HrCopyWeekInput = z.infer<typeof hrCopyWeekInputSchema>;
export const hrClockInInputSchema = z.object({
  qr_code: z.string().optional(),
  latitude: z.number().finite().min(-90).max(90).optional(),
  longitude: z.number().finite().min(-180).max(180).optional(),
  store_id: z.string().optional(),
});
export type HrClockInInput = z.infer<typeof hrClockInInputSchema>;
/** Payload JSON contenuto nel campo qr_code dell'ingresso autenticato. */
export const hrClockInQrPayloadSchema = z.object({
  token_id: z.string().uuid(),
  totp_code: z.string().regex(/^[a-f0-9]{8}$/),
  store_id: z.string().uuid(),
}).strict();
export type HrClockInQrPayload = z.infer<typeof hrClockInQrPayloadSchema>;
/** Proiezione gestionale: il segreto TOTP non lascia mai il servizio. */
export const hrQrTokenSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  store_name: z.string().nullable().optional(),
  rotation_interval_seconds: z.number(),
  is_active: z.boolean(),
  created_at: wireTimestamp,
  expires_at: wireTimestamp.nullable(),
});
export type HrQrToken = z.infer<typeof hrQrTokenSchema>;
export const hrQrCodeSchema = z.object({
  qr_data: z.string().startsWith('/'),
  totp_code: z.string(),
  remaining_seconds: z.number(),
  store_name: z.string(),
  token_id: z.string().uuid(),
});
export type HrQrCode = z.infer<typeof hrQrCodeSchema>;
export const hrManualAttendanceInputSchema = z.object({
  user_id: z.string().min(1),
  store_id: z.string().min(1),
  notes: z.string(),
  clock_in: z.string().optional(),
  clock_out: z.string().optional(),
});
export type HrManualAttendanceInput = z.infer<typeof hrManualAttendanceInputSchema>;

// ============================================================================
// 1) Catalogo tipologie richiesta — GET /hr/request-types
// ============================================================================

export const hrRequestTypeCategorySchema = z.enum([
  'ferie',
  'permesso',
  'malattia',
  'congedo',
  'organizzativa',
  'altro',
]);
export type HrRequestTypeCategory = z.infer<typeof hrRequestTypeCategorySchema>;
/** Ordine di presentazione delle categorie (picker richieste, instradamento). */
export const HR_REQUEST_TYPE_CATEGORIES = hrRequestTypeCategorySchema.options;
export const HR_REQUEST_TYPE_CATEGORY_LABELS: Record<HrRequestTypeCategory, string> = {
  ferie: 'Ferie',
  permesso: 'Permessi',
  malattia: 'Malattia / Infortunio',
  congedo: 'Congedi',
  organizzativa: 'Organizzative',
  altro: 'Altro',
};

export const hrRequestTypeUnitSchema = z.enum(['day', 'hours']);
export type HrRequestTypeUnit = z.infer<typeof hrRequestTypeUnitSchema>;

/**
 * Domini di instradamento (insieme chiuso): ogni tipologia appartiene a un
 * dominio e ogni dominio ha un settore predefinito per tenant
 * (hr_routing_domains, seminato HR → "Risorse umane", Logistica → "Magazzino",
 * modificabile). La destinazione di una tipologia = settore del dominio (se
 * "segue il dominio") ∪ settori aggiuntivi; mai vuota se la tipologia è attiva.
 */
export const hrRoutingDomainSchema = z.enum(['hr', 'logistica']);
export type HrRoutingDomain = z.infer<typeof hrRoutingDomainSchema>;
export const HR_ROUTING_DOMAINS = hrRoutingDomainSchema.options;
export const HR_ROUTING_DOMAIN_LABELS: Record<HrRoutingDomain, string> = {
  hr: 'Risorse umane',
  logistica: 'Logistica',
};

/**
 * Task #4268 — bundle permessi per dominio di instradamento: UNICO punto
 * condiviso BE↔FE. Il badge di settore dice CHI viene interpellato; questi
 * permessi danno il potere di agire. All'assegnazione del badge vengono
 * proposti (spunta attiva, mai concessi in silenzio) come concessioni
 * permesso × portata sul recinto del manager.
 */
export interface HrDomainBundlePermission {
  code: string;
  /** Etichetta breve, orientata all'azione ("Approvare le richieste HR"). */
  label: string;
  /** Il permesso che rende il manager capace di decidere (usato per l'allarme "riceve ma non può agire"). */
  is_approval: boolean;
}
export const HR_DOMAIN_PERMISSION_BUNDLES: Record<HrRoutingDomain, ReadonlyArray<HrDomainBundlePermission>> = {
  hr: [
    { code: 'hr.requests.view', label: 'Vedere le richieste HR', is_approval: false },
    { code: 'hr.requests.approve', label: 'Approvare o respingere le richieste HR', is_approval: true },
  ],
  logistica: [
    { code: 'transfer.request', label: 'Richiedere trasferimenti', is_approval: false },
    { code: 'transfer.authorize', label: 'Autorizzare trasferimenti', is_approval: true },
    { code: 'transfer.send', label: 'Inviare trasferimenti', is_approval: false },
    { code: 'transfer.load', label: 'Caricare trasferimenti in arrivo', is_approval: false },
  ],
};
/** Permesso di approvazione per dominio (derivato dal bundle, mai duplicato altrove). */
export const HR_DOMAIN_APPROVAL_PERMISSION: Record<HrRoutingDomain, string> = {
  hr: HR_DOMAIN_PERMISSION_BUNDLES.hr.find((b) => b.is_approval)?.code ?? 'hr.requests.approve',
  logistica: HR_DOMAIN_PERMISSION_BUNDLES.logistica.find((b) => b.is_approval)?.code ?? 'transfer.authorize',
};
/** Conseguenza leggibile di un settore scoperto, per dominio. */
export const HR_DOMAIN_GAP_CONSEQUENCE: Record<HrRoutingDomain, string> = {
  hr: 'le richieste HR arrivano agli admin',
  logistica: 'i trasferimenti arrivano agli admin',
};

/**
 * Recapito verso un settore di destinazione — puro instradamento, mai poteri:
 *  - notifica: i manager del settore ricevono solo la notifica informativa
 *    ("per conoscenza"); non entrano in routing_snapshot.recipients e la
 *    richiesta non compare nella loro inbox come pratica;
 *  - notifica_e_richiesta: notifica + la richiesta entra nella loro inbox
 *    (destinatari dello snapshot, come per il settore predefinito del dominio).
 * Il settore predefinito del dominio vale sempre notifica_e_richiesta; le
 * tipologie acknowledgment_only valgono notifica su ogni destinazione.
 */
export const hrRoutingDeliverySchema = z.enum(['notifica', 'notifica_e_richiesta']);
export type HrRoutingDelivery = z.infer<typeof hrRoutingDeliverySchema>;
export const HR_ROUTING_DELIVERIES = hrRoutingDeliverySchema.options;
export const HR_ROUTING_DELIVERY_LABELS: Record<HrRoutingDelivery, string> = {
  notifica: 'Solo notifica',
  notifica_e_richiesta: 'Notifica + richiesta',
};

/** Riferimento a un settore di destinazione (id + nome + stato). */
export const hrRoutingDepartmentRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_active: z.boolean(),
});
export type HrRoutingDepartmentRef = z.infer<typeof hrRoutingDepartmentRefSchema>;

/**
 * Settore di destinazione con il recapito EFFETTIVO (già ridotto a `notifica`
 * se la tipologia è acknowledgment_only; il predefinito del dominio è
 * `notifica_e_richiesta` altrimenti).
 */
export const hrRoutingDestinationSchema = hrRoutingDepartmentRefSchema.extend({
  delivery: hrRoutingDeliverySchema,
});
export type HrRoutingDestination = z.infer<typeof hrRoutingDestinationSchema>;

/** Voce di hr_request_type_departments: settore aggiuntivo + recapito scelto. */
export const hrRoutingAdditionalDepartmentInputSchema = z.object({
  department_id: z.string().uuid(),
  delivery: hrRoutingDeliverySchema,
});
export type HrRoutingAdditionalDepartmentInput = z.infer<typeof hrRoutingAdditionalDepartmentInputSchema>;

// ---------------------------------------------------------------------------
// Flusso a fasi (Task #4302) — la tipologia di sistema "Trasferimento merce"
// instrada per FASE: ogni fase ha destinazione e interruttore propri.
// ---------------------------------------------------------------------------

/** Codice della tipologia di sistema che instrada per fase (dominio logistica). */
export const HR_ROUTING_FLOW_TYPE_CODE = 'trasferimento_merce';
/** Etichetta del macro-gruppo che raccoglie le fasi nella tab Instradamento. */
export const HR_ROUTING_FLOW_GROUP_LABEL = 'Trasferimento interno';

/** Fasi del flusso, nell'ordine del ciclo di vita (chiavi = fasi pendenti del trasferimento + chiusura). */
export const hrRoutingFlowPhaseSchema = z.enum(['authorize', 'send', 'load', 'closed']);
export type HrRoutingFlowPhase = z.infer<typeof hrRoutingFlowPhaseSchema>;
export const HR_ROUTING_FLOW_PHASES = hrRoutingFlowPhaseSchema.options;

export interface HrRoutingFlowPhaseMeta {
  /** Stato della pratica che apre la fase ("Richiesta creata"). */
  label: string;
  /** Chi riceve e su quale sede ("sede origine → chi autorizza"). */
  hint: string;
  /** Permesso operativo della fase (riserva); null per la chiusura (solo informativa). */
  permission: string | null;
  /** Fase a sola comunicazione: ogni destinazione vale `notifica`. */
  acknowledgment_only: boolean;
}
export const HR_ROUTING_FLOW_PHASE_META: Record<HrRoutingFlowPhase, HrRoutingFlowPhaseMeta> = {
  authorize: { label: 'Richiesta creata', hint: 'sede di origine → chi autorizza', permission: 'transfer.authorize', acknowledgment_only: false },
  send: { label: 'Autorizzata', hint: 'sede di origine → chi invia', permission: 'transfer.send', acknowledgment_only: false },
  load: { label: 'Spedita', hint: 'sede di destinazione → chi carica', permission: 'transfer.load', acknowledgment_only: false },
  closed: { label: 'Chiusa', hint: 'esito → chi va informato', permission: null, acknowledgment_only: true },
};

/**
 * Fase del flusso con destinazione risolta (stessa forma della tipologia:
 * predefinito del dominio sempre esposto, aggiuntivi per fase, unione
 * effettiva). `permission`/`acknowledgment_only` sono i valori di
 * HR_ROUTING_FLOW_PHASE_META, esposti per non farli ricalcolare al client.
 */
export const hrRoutingFlowPhaseRowSchema = z.object({
  phase: hrRoutingFlowPhaseSchema,
  label: z.string(),
  hint: z.string(),
  permission: z.string().nullable(),
  acknowledgment_only: z.boolean(),
  follows_domain: z.boolean(),
  is_active: z.boolean(),
  domain_default_department: hrRoutingDestinationSchema.nullable(),
  additional_departments: z.array(hrRoutingDestinationSchema),
  destination_departments: z.array(hrRoutingDestinationSchema),
});
export type HrRoutingFlowPhaseRow = z.infer<typeof hrRoutingFlowPhaseRowSchema>;

/** Riga di hr_request_types (SELECT *). */
export const hrRequestTypeSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    code: z.string(),
    label: z.string(),
    /** Insieme chiuso (CHECK hr_request_types_category_check). */
    category: hrRequestTypeCategorySchema,
    domain: hrRoutingDomainSchema,
    /** La destinazione include il settore predefinito del dominio. */
    follows_domain: z.boolean(),
    approval_permission: z.string(),
    paid: z.boolean(),
    unit: z.string(),
    requires_attachment: z.boolean(),
    blocks_shift: z.boolean(),
    blocks_availability: z.boolean(),
    acknowledgment_only: z.boolean(),
    is_active: z.boolean(),
    sort_order: z.number(),
    legacy_type: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
  })
  .passthrough();
export type HrRequestType = z.infer<typeof hrRequestTypeSchema>;

/**
 * GET /hr/request-types — tipologia con destinazione risolta: settore
 * predefinito del dominio (sempre esposto, entra nell'unione solo se
 * follows_domain), settori aggiuntivi (junction hr_request_type_departments)
 * e unione effettiva.
 */
export const hrRequestTypeWithRoutingSchema = hrRequestTypeSchema.extend({
  /** Predefinito del dominio (null solo se il dominio non è configurato). */
  domain_default_department: hrRoutingDestinationSchema.nullable(),
  additional_departments: z.array(hrRoutingDestinationSchema),
  /** Unione effettiva (dominio ∪ aggiuntivi), deduplicata, ordinata per nome. */
  destination_departments: z.array(hrRoutingDestinationSchema),
  /**
   * Solo per la tipologia flusso (HR_ROUTING_FLOW_TYPE_CODE): le fasi con la
   * propria destinazione, nell'ordine di HR_ROUTING_FLOW_PHASES. Per il flusso
   * i campi di tipologia (follows_domain/additional/destination) restano quelli
   * della riga ma l'instradamento effettivo è quello delle fasi. null altrove.
   */
  flow_phases: z.array(hrRoutingFlowPhaseRowSchema).nullable(),
});
export type HrRequestTypeWithRouting = z.infer<typeof hrRequestTypeWithRoutingSchema>;

/** GET /hr/routing/domains — configurazione per dominio (settore predefinito). */
export const hrRoutingDomainConfigSchema = z.object({
  domain: hrRoutingDomainSchema,
  label: z.string(),
  default_department: hrRoutingDepartmentRefSchema,
  /** Tipologie del dominio (attive) che seguono il dominio. */
  following_type_count: z.number(),
  /** Tutte le tipologie del dominio (attive e disattivate). */
  request_type_count: z.number(),
  /** Tipologie attive del dominio (la riga di dominio conta queste). */
  active_type_count: z.number(),
  /**
   * Tipologie attive che deviano dal predefinito: non lo seguono oppure hanno
   * settori aggiuntivi. `active_type_count - deviating_type_count` = attive che
   * arrivano SOLO al predefinito.
   */
  deviating_type_count: z.number(),
});
export type HrRoutingDomainConfig = z.infer<typeof hrRoutingDomainConfigSchema>;

/**
 * Body PUT /hr/routing/request-types — aggiornamento massivo dell'instradamento
 * in UNA transazione. Le tipologie coinvolte sono scelte per dominio, per
 * categoria di un dominio o per elenco esplicito; l'azione è "segui il
 * predefinito" (follows_domain=true, nessun aggiuntivo) oppure "stesse
 * destinazioni" (replace integrale, ogni settore aggiuntivo con il proprio
 * recapito). Ogni tipologia è validata come nel PATCH singolo (settori attivi,
 * mai orfanare tipologie attive non a sola comunicazione); un solo errore
 * annulla tutto.
 */
export const hrRoutingBulkScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('domain'), domain: hrRoutingDomainSchema }),
  z.object({ kind: z.literal('category'), domain: hrRoutingDomainSchema, category: hrRequestTypeCategorySchema }),
  z.object({ kind: z.literal('request_types'), request_type_ids: z.array(z.string().uuid()).min(1).max(200) }),
]);
export type HrRoutingBulkScope = z.infer<typeof hrRoutingBulkScopeSchema>;

export const hrRoutingBulkActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('follow_domain') }),
  z.object({
    kind: z.literal('set_destination'),
    follows_domain: z.boolean(),
    additional_departments: z.array(hrRoutingAdditionalDepartmentInputSchema).max(50),
  }),
]);
export type HrRoutingBulkAction = z.infer<typeof hrRoutingBulkActionSchema>;

export const hrRoutingBulkUpdateInputSchema = z.object({
  scope: hrRoutingBulkScopeSchema,
  action: hrRoutingBulkActionSchema,
  /** Se true (default) le tipologie disattivate dell'ambito restano intoccate. */
  active_only: z.boolean().default(true),
});
export type HrRoutingBulkUpdateInput = z.input<typeof hrRoutingBulkUpdateInputSchema>;
export type HrRoutingBulkUpdateParsed = z.output<typeof hrRoutingBulkUpdateInputSchema>;

/** Risposta PUT /hr/routing/request-types: le tipologie toccate, già con destinazione risolta. */
export const hrRoutingBulkUpdateResultSchema = z.object({
  updated: z.array(hrRequestTypeWithRoutingSchema),
});
export type HrRoutingBulkUpdateResult = z.infer<typeof hrRoutingBulkUpdateResultSchema>;

/** Body PUT /hr/routing/domains/:domain. */
export const hrRoutingDomainUpdateInputSchema = z.object({
  default_department_id: z.string().uuid(),
});
export type HrRoutingDomainUpdateInput = z.infer<typeof hrRoutingDomainUpdateInputSchema>;

/** GET /hr/routing/by-department — quadro per settore: quali tipologie vi arrivano e perché. */
export const hrRoutingByDepartmentTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  label: z.string(),
  domain: hrRoutingDomainSchema,
  category: hrRequestTypeCategorySchema,
  is_active: z.boolean(),
  /** Come la tipologia arriva al settore: predefinito del dominio o aggiuntivo. */
  via: z.enum(['domain', 'additional']),
  /** Recapito effettivo verso il settore. */
  delivery: hrRoutingDeliverySchema,
});
export type HrRoutingByDepartmentType = z.infer<typeof hrRoutingByDepartmentTypeSchema>;

export const hrRoutingByDepartmentSchema = z.object({
  department_id: z.string(),
  department_name: z.string(),
  is_active: z.boolean(),
  system_key: z.string().nullable(),
  request_types: z.array(hrRoutingByDepartmentTypeSchema),
});
export type HrRoutingByDepartment = z.infer<typeof hrRoutingByDepartmentSchema>;

// ============================================================================
// 2) Routing destinatari — veste + snapshot persistito (§5.3 design doc)
// ============================================================================

export const hrRecipientRoleSchema = z.enum([
  /** Responsabile di settore del livello interpellato (il livello è in `level`). */
  'responsabile',
  /** Storico: vecchia veste "responsabile generale" (non più prodotta). */
  'responsabile_generale',
  'fallback_permesso',
  'admin_tenant',
]);
export type HrRecipientRole = z.infer<typeof hrRecipientRoleSchema>;

export const hrResolvedRecipientSchema = z.object({
  user_id: z.string(),
  user_name: z.string(),
  role: hrRecipientRoleSchema,
  /** Livello di subentro del responsabile (solo veste `responsabile`); null per fallback/admin. */
  level: z.number().int().min(1).nullable().default(null),
  department_id: z.string().nullable(),
  department_name: z.string().nullable(),
  /** Motivazione leggibile: "Responsabile livello 1 settore Magazzino (sede Milano) per Mario Rossi". */
  reason: z.string(),
});
export type HrResolvedRecipient = z.infer<typeof hrResolvedRecipientSchema>;

/** Perché un livello è stato saltato dal resolver (subentro). */
export const hrRoutingSkippedLevelReasonSchema = z.enum([
  /** Nessun decisore del livello copre le sedi interessate col recinto. */
  'fuori_recinto',
  /** L'unico decisore del livello è il richiedente stesso. */
  'richiedente',
  /** Decisore inattivo o ruolo senza flag manager. */
  'non_eleggibile',
  /** Decisore senza il permesso di approvazione della tipologia. */
  'senza_permesso',
  /** Decisore assente (assenza HR approvata in corso o sospensione manuale). */
  'assente',
]);
export type HrRoutingSkippedLevelReason = z.infer<typeof hrRoutingSkippedLevelReasonSchema>;

export const hrRoutingSkippedLevelSchema = z.object({
  level: z.number().int().min(1),
  reason: hrRoutingSkippedLevelReasonSchema,
  /** Testo leggibile: "Mario Rossi assente fino al 05/10/2026". */
  detail: z.string(),
});
export type HrRoutingSkippedLevel = z.infer<typeof hrRoutingSkippedLevelSchema>;

/**
 * Esito per settore della scelta del livello: quale livello ha ricevuto la
 * pratica e quali livelli inferiori sono stati saltati (e perché). Registrato
 * nello snapshot per mostrare il motivo del subentro sulla pratica.
 */
export const hrRoutingTakeoverSchema = z.object({
  department_id: z.string(),
  department_name: z.string(),
  /** Livello che riceve la pratica; null = nessun livello raggiungibile (catena fallback). */
  resolved_level: z.number().int().min(1).nullable(),
  skipped: z.array(hrRoutingSkippedLevelSchema),
});
export type HrRoutingTakeover = z.infer<typeof hrRoutingTakeoverSchema>;

/** Da dove vengono le sedi interessate passate al resolver. */
export const hrInterestedStoreSourceSchema = z.enum(['impacted_shifts', 'attendance', 'user_stores', 'explicit']);
export type HrInterestedStoreSource = z.infer<typeof hrInterestedStoreSourceSchema>;

/**
 * Provenienza delle sedi interessate, registrata nello snapshot ("instradata
 * per: turno / timbratura / tutte le sedi"): turni nella finestra → timbrature
 * nella finestra (o ultimi giorni per richieste future) → tutte le sedi
 * dell'utente. `esplicita` = sedi passate dal chiamante (es. POS trasferimenti).
 */
export const hrRoutingReasonSchema = z.enum(['turno', 'timbratura', 'tutte_le_sedi', 'esplicita']);
export type HrRoutingReason = z.infer<typeof hrRoutingReasonSchema>;
export const HR_ROUTING_REASON_LABELS: Record<HrRoutingReason, string> = {
  turno: 'Turni nella finestra',
  timbratura: 'Timbrature recenti',
  tutte_le_sedi: "Tutte le sedi dell'utente",
  esplicita: 'Sedi indicate',
};
export const HR_ROUTING_REASON_BY_SOURCE: Record<HrInterestedStoreSource, HrRoutingReason> = {
  impacted_shifts: 'turno',
  attendance: 'timbratura',
  user_stores: 'tutte_le_sedi',
  explicit: 'esplicita',
};

/** Sede interessata (id + nome) — per snapshot, motivazioni e metadati notifica. */
export const hrInterestedStoreSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
});
export type HrInterestedStore = z.infer<typeof hrInterestedStoreSchema>;

/**
 * Esito del resolver destinatari condiviso ("chi risponde di X per l'utente Y"):
 * lista tipizzata + regola applicata + settore/sedi usati. Indipendente da
 * hr_requests: lo stesso DTO serve HR, POS trasferimenti e domini futuri.
 */
export const hrRoutingDestinationDepartmentSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type HrRoutingDestinationDepartment = z.infer<typeof hrRoutingDestinationDepartmentSchema>;

export const hrResponsibleResolutionSchema = z.object({
  recipients: z.array(hrResolvedRecipientSchema),
  /** Settori di destinazione effettivamente iterati (attivi). */
  /** Responsabili con modalità "informato" dei settori di destinazione: notifica per conoscenza, mai decisori. */
  informed: z.array(hrResolvedRecipientSchema),
  /** Per settore: livello interpellato e livelli saltati (motivo del subentro). */
  takeovers: z.array(hrRoutingTakeoverSchema),
  destination_departments: z.array(hrRoutingDestinationDepartmentSchema),
  interested_stores: z.array(hrInterestedStoreSchema),
  interested_store_source: hrInterestedStoreSourceSchema,
  routing_reason: hrRoutingReasonSchema,
});
export type HrResponsibleResolution = z.infer<typeof hrResponsibleResolutionSchema>;

/**
 * jsonb hr_requests.routing_snapshot — risoluzione al submit, per audit/UI.
 * I campi oltre a resolved_at/recipients sono opzionali: gli snapshot nati
 * prima della regola per tipologia restano validi e NON vengono ri-risolti.
 */
/** Chi ha chiuso la pratica (primo che agisce) — registrato nello snapshot per gli altri destinatari. */
export const hrRoutingActedBySchema = z.object({
  user_id: z.string(),
  user_name: z.string(),
  status: z.string(),
  acted_at: z.string(),
});
export type HrRoutingActedBy = z.infer<typeof hrRoutingActedBySchema>;

/**
 * Criterio storico con cui la richiesta fu instradata. Lo snapshot è l'UNICA
 * fonte per lo storico: le richieste nate con la vecchia regola a tre valori
 * conservano qui la propria copia (`legacy_rule`), copiata dalla migrazione
 * prima del DROP della colonna; il resolver non la legge mai.
 */
export const hrRoutingCriterionSchema = z.object({
  kind: z.enum(['destination_departments', 'legacy_rule']),
  /** Valore storico della vecchia regola (settore_richiedente | settore_fisso | solo_permesso). */
  legacy_rule: z.string().nullable().optional(),
  /** Come il criterio è finito nello snapshot: risolto al submit o copiato dalla migrazione. */
  source: z.enum(['resolved', 'migrated_from_type']),
});
export type HrRoutingCriterion = z.infer<typeof hrRoutingCriterionSchema>;

export const hrRoutingSnapshotSchema = z.object({
  resolved_at: z.string(),
  recipients: z.array(hrResolvedRecipientSchema),
  /**
   * Destinatari "per conoscenza" (settori con recapito `notifica`): hanno
   * ricevuto solo la notifica informativa, non decidono e non sono destinatari
   * della pratica. Assente negli snapshot nati prima del recapito per settore.
   */
  informed: z.array(hrResolvedRecipientSchema).optional(),
  criterion: hrRoutingCriterionSchema.optional(),
  destination_departments: z.array(hrRoutingDestinationDepartmentSchema).optional(),
  /** Settore storico (vecchia regola settore_fisso) — solo lettura storica. */
  department_id: z.string().nullable().optional(),
  department_name: z.string().nullable().optional(),
  interested_stores: z.array(hrInterestedStoreSchema).optional(),
  interested_store_source: hrInterestedStoreSourceSchema.optional(),
  routing_reason: hrRoutingReasonSchema.optional(),
  /** Subentro tra livelli (assente negli snapshot nati prima dei livelli). */
  takeovers: z.array(hrRoutingTakeoverSchema).optional(),
  acted_by: hrRoutingActedBySchema.nullable().optional(),
});
export type HrRoutingSnapshot = z.infer<typeof hrRoutingSnapshotSchema>;

// ============================================================================
// 3) Richieste — GET/POST /hr/requests, GET /hr/requests/:id
// ============================================================================

/** Riga `SELECT r.*` di hr_requests + alias requester/admin (listRequests). */
export const hrRequestRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    requester_user_id: z.string(),
    /** Legacy: derivato dal catalogo (legacy_type o code) — non più CHECK DB. */
    type: z.string(),
    type_id: z.string().nullable(),
    status: z.string(),
    priority: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    /** Deprecata: = date_from (mantenuta per retrocompatibilità consumer). */
    target_date: wireDate.nullable(),
    date_from: wireDate.nullable(),
    date_to: wireDate.nullable(),
    time_from: z.string().nullable(),
    time_to: z.string().nullable(),
    attachment_file_id: z.string().nullable(),
    routing_snapshot: z.unknown().nullable(),
    current_shift_assignment_id: z.string().nullable(),
    requested_shift_template_id: z.string().nullable(),
    swap_with_user_id: z.string().nullable(),
    assigned_admin_id: z.string().nullable(),
    resolved_at: wireTimestamp.nullable(),
    resolved_by: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    // alias joinati (listRequests / getRequestById)
    requester_first_name: z.string().optional(),
    requester_last_name: z.string().optional(),
    requester_email: z.string().optional(),
    admin_first_name: z.string().nullable().optional(),
    admin_last_name: z.string().nullable().optional(),
    // alias catalogo (join hr_request_types)
    type_code: z.string().nullable().optional(),
    type_label: z.string().nullable().optional(),
    type_category: z.string().nullable().optional(),
    type_unit: z.string().nullable().optional(),
    type_requires_attachment: z.boolean().nullable().optional(),
    type_acknowledgment_only: z.boolean().nullable().optional(),
  })
  .passthrough();
export type HrRequestRow = z.infer<typeof hrRequestRowSchema>;

/** GET /hr/requests/:id — dettaglio con veste del lettore (se destinatario). */
export const hrRequestDetailSchema = hrRequestRowSchema.extend({
  /** Veste con cui la richiesta è arrivata al lettore corrente (null = nessuna). */
  viewer_recipient: hrResolvedRecipientSchema.nullable().optional(),
});
export type HrRequestDetail = z.infer<typeof hrRequestDetailSchema>;

// ============================================================================
// 4) Impact preview — GET /hr/requests/:id/impact (§4.4 design doc)
// ============================================================================

export const hrImpactSeveritySchema = z.enum(['none', 'low', 'medium', 'high', 'critical']);
export type HrImpactSeverity = z.infer<typeof hrImpactSeveritySchema>;

export const hrImpactedShiftSchema = z.object({
  assignment_id: z.string(),
  date: wireDate,
  store_id: z.string(),
  store_name: z.string(),
  template_name: z.string(),
  template_start_time: z.string(),
  template_end_time: z.string(),
  template_color: z.string().nullable(),
  is_overnight: z.boolean(),
  status: z.string(),
  impacted_hours: z.number(),
  severity: hrImpactSeveritySchema,
});
export type HrImpactedShift = z.infer<typeof hrImpactedShiftSchema>;

export const hrRequestImpactSchema = z.object({
  request_id: z.string(),
  blocks_shift: z.boolean(),
  blocks_availability: z.boolean(),
  shifts: z.array(hrImpactedShiftSchema),
  total_shifts: z.number(),
  total_hours: z.number(),
  stores: z.array(z.object({ store_id: z.string(), store_name: z.string(), shifts: z.number() })),
  severity: hrImpactSeveritySchema,
  warnings: z.array(z.string()),
  /** Buchi di copertura che l'approvazione creerebbe (solo store con fabbisogno). */
  coverage_gaps: z.array(
    z.object({
      store_id: z.string(),
      store_name: z.string(),
      date: wireDate,
      time_from: z.string(),
      time_to: z.string(),
      required_staff: z.number(),
      planned_after: z.number(),
      coverage_pct_after: z.number(),
    })
  ),
});
export type HrRequestImpact = z.infer<typeof hrRequestImpactSchema>;

// ============================================================================
// 5) Conflitti turno↔assenza — pre-check assegnazione (§4.3 design doc)
// ============================================================================

export const hrShiftConflictRefSchema = z.object({
  request_id: z.string(),
  type_code: z.string().nullable(),
  type_label: z.string(),
  status: z.string(),
  date_from: wireDate,
  date_to: wireDate,
  time_from: z.string().nullable(),
  time_to: z.string().nullable(),
});
export type HrShiftConflictRef = z.infer<typeof hrShiftConflictRefSchema>;

export const hrShiftConflictCheckSchema = z.object({
  blocking: z.array(hrShiftConflictRefSchema),
  warnings: z.array(hrShiftConflictRefSchema),
});
export type HrShiftConflictCheck = z.infer<typeof hrShiftConflictCheckSchema>;

/**
 * Esito POST/PUT /hr/shift-assignments (Task #3876 — DTO condiviso al posto
 * del tipo locale FE). Single: riga salvata (passthrough) + `conflict_warnings`
 * non bloccanti. Bulk: `{ created, errors }` dove ogni errore porta `error`
 * (messaggio) ed eventualmente `code`/`conflicts` strutturati.
 */
/** Riga di GET /hr/requests/:id/comments (SELECT c.* + JOIN users: nome e ruolo autore). */
export const hrRequestCommentRowSchema = z
  .object({
    id: z.string(),
    request_id: z.string(),
    user_id: z.string(),
    comment: z.string(),
    is_internal: z.boolean(),
    first_name: z.string(),
    last_name: z.string(),
    user_role: z.string(),
    created_at: wireTimestamp,
  })
  .passthrough();
export type HrRequestCommentRow = z.infer<typeof hrRequestCommentRowSchema>;

/** Stato disponibilità di una risorsa per una data (picker pianificazione). */
export const hrUserDayAvailabilitySchema = z.object({
  user_id: z.string(),
  date: hrCalendarDateSchema,
  status: z.enum(['available', 'blocked', 'at_risk']),
  /** Presente per blocked/at_risk: tipologia e riferimento richiesta. */
  request_id: z.string().nullable(),
  type_label: z.string().nullable(),
  time_from: z.string().nullable(),
  time_to: z.string().nullable(),
});
export type HrUserDayAvailability = z.infer<typeof hrUserDayAvailabilitySchema>;

// ============================================================================
// 6) Turni — riga assignment con alias griglia (listShiftAssignments)
// ============================================================================

/** Pausa di un template turno (HH:MM[:SS]), N per template. */
export const hrShiftTemplateBreakSchema = z.object({
  break_start: z.string(),
  break_end: z.string(),
  /** true = pausa retribuita (conta nelle ore lavorate); false = non retribuita (default). */
  is_paid: z.boolean().optional().default(false),
});
export type HrShiftTemplateBreak = z.infer<typeof hrShiftTemplateBreakSchema>;

export const hrShiftAssignmentRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    user_id: z.string(),
    shift_template_id: z.string(),
    store_id: z.string(),
    date: hrCalendarDateSchema,
    assignment_date: hrCalendarDateSchema,
    status: z.string(),
    notes: z.string().nullable(),
    assigned_by: z.string().nullable(),
    override_request_id: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    // alias joinati (listShiftAssignments)
    template_code: z.string().optional(),
    template_name: z.string().optional(),
    template_start_time: z.string().optional(),
    template_end_time: z.string().optional(),
    template_color: z.string().nullable().optional(),
    template_is_overnight: z.boolean().optional(),
    template_breaks: z.array(hrShiftTemplateBreakSchema).nullable().optional(),
    template_break_duration_minutes: z.number().nullable().optional(),
    template_unpaid_break_duration_minutes: z.number().nullable().optional(),
    user_first_name: z.string().optional(),
    user_last_name: z.string().optional(),
    user_email: z.string().optional(),
    store_name: z.string().optional(),
  })
  .passthrough();
export type HrShiftAssignmentRow = z.infer<typeof hrShiftAssignmentRowSchema>;

export const hrShiftSaveResultSchema = hrShiftAssignmentRowSchema.extend({
  conflict_warnings: z.array(hrShiftConflictRefSchema),
});
export type HrShiftSaveResult = z.infer<typeof hrShiftSaveResultSchema>;
export const hrShiftBulkResultSchema = z.object({
  created: z.array(hrShiftSaveResultSchema),
  replaced: z.array(hrShiftSaveResultSchema),
  skipped: z.array(z.object({ user_id: z.string(), date: hrCalendarDateSchema, reason: z.string() })),
  errors: z.array(z.object({
    data: hrShiftAssignmentInputSchema,
    error: z.string(),
    code: z.string().optional(),
    conflicts: z.array(hrShiftConflictRefSchema).optional(),
  })),
});
export type HrShiftBulkResult = z.infer<typeof hrShiftBulkResultSchema>;

/** GET /hr/shifts/assignments — griglia + mappa disponibilità per il picker. */
export const hrShiftAssignmentsGridSchema = z.object({
  assignments: z.array(hrShiftAssignmentRowSchema),
  /** Disponibilità per (user, date) nell'intervallo richiesto (assenze appr./pending). */
  availability: z.array(hrUserDayAvailabilitySchema),
});
export type HrShiftAssignmentsGrid = z.infer<typeof hrShiftAssignmentsGridSchema>;

/** POST bulk — esito con celle saltate (copy-week / bulk). */
export const hrCopyWeekResultSchema = z.object({
  copied: z.number(),
  /** Assegnazioni sovrascritte quando la duplicazione è invocata con override. */
  replaced: z.number().optional(),
  skipped: z.array(
    z.object({
      user_id: z.string(),
      date: hrCalendarDateSchema,
      reason: z.string(),
    })
  ),
  errors: z.array(z.object({ user_id: z.string(), date: hrCalendarDateSchema, error: z.string() })),
});
export type HrCopyWeekResult = z.infer<typeof hrCopyWeekResultSchema>;

// ============================================================================
// 7) Fabbisogno e copertura per store (§7.2–7.3 design doc)
// ============================================================================

export const hrStaffingRequirementSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    store_id: z.string(),
    weekday: z.number().nullable(),
    date: wireDate.nullable(),
    time_from: z.string(),
    time_to: z.string(),
    required_staff: z.number(),
    department_id: z.string().nullable(),
    is_active: z.boolean(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
  })
  .passthrough();
export type HrStaffingRequirement = z.infer<typeof hrStaffingRequirementSchema>;

export const hrCoverageAnomalyTypeSchema = z.enum([
  'open_uncovered',
  'understaffed',
  'shift_outside_opening',
  'shift_on_closed_day',
  'overstaffed',
]);
export type HrCoverageAnomalyType = z.infer<typeof hrCoverageAnomalyTypeSchema>;

export const hrCoverageSegmentSchema = z.object({
  time_from: z.string(),
  time_to: z.string(),
  required_staff: z.number(),
  planned_staff: z.number(),
  coverage_pct: z.number(),
  /** ok | warning | critical (soglie hr_settings). */
  level: z.enum(['ok', 'warning', 'critical', 'over']),
});
export type HrCoverageSegment = z.infer<typeof hrCoverageSegmentSchema>;

export const hrStoreCoverageDaySchema = z.object({
  date: wireDate,
  weekday: z.number(),
  /** Fascia di apertura del giorno (null = chiuso/festività). */
  open_from: z.string().nullable(),
  open_to: z.string().nullable(),
  break_start: z.string().nullable(),
  break_end: z.string().nullable(),
  closed: z.boolean(),
  holiday_name: z.string().nullable(),
  segments: z.array(hrCoverageSegmentSchema),
  /** % copertura complessiva del giorno (null = nessun fabbisogno configurato). */
  day_coverage_pct: z.number().nullable(),
  day_level: z.enum(['ok', 'warning', 'critical', 'none']),
  anomalies: z.array(
    z.object({
      type: hrCoverageAnomalyTypeSchema,
      severity: z.enum(['info', 'warning', 'critical']),
      message: z.string(),
      time_from: z.string().nullable(),
      time_to: z.string().nullable(),
      assignment_id: z.string().nullable(),
    })
  ),
});
export type HrStoreCoverageDay = z.infer<typeof hrStoreCoverageDaySchema>;

export const hrStoreCoverageSchema = z.object({
  store_id: z.string(),
  store_name: z.string(),
  days: z.array(hrStoreCoverageDaySchema),
});
export type HrStoreCoverage = z.infer<typeof hrStoreCoverageSchema>;

/** GET /hr/shifts/coverage — lente copertura (uno o più store). */
export const hrCoverageResponseSchema = z.object({
  stores: z.array(hrStoreCoverageSchema),
  thresholds: z.object({ warning: z.number(), critical: z.number() }),
});
export type HrCoverageResponse = z.infer<typeof hrCoverageResponseSchema>;

/** GET /hr/shifts/to-recover — coda turni da ricoprire (status=override). */
export const hrShiftToRecoverSchema = hrShiftAssignmentRowSchema.extend({
  override_type_label: z.string().nullable().optional(),
  override_requester_name: z.string().nullable().optional(),
});
export type HrShiftToRecover = z.infer<typeof hrShiftToRecoverSchema>;

// ============================================================================
// 8) Responsabilità per settore — organigramma, membership, livelli di
//    subentro con modalità per responsabile, report migrazione.
// ============================================================================

/** Livello di subentro (1 = interpellato per primo). */
export const hrResponsibilityLevelSchema = z.number().int().min(1).max(9);
export type HrResponsibilityLevel = z.infer<typeof hrResponsibilityLevelSchema>;

/**
 * Posizione di un responsabile in un settore: livello + modalità.
 * `decides` = riceve la pratica e può deciderla (partecipa alla scelta del
 * livello e alla disgiunzione dei recinti); `informed` = riceve la notifica
 * per conoscenza. Almeno uno dei due è vero. `suspended_until` (YYYY-MM-DD,
 * inclusa) = sospensione manuale: trattato come assente dal resolver.
 */
export const hrResponsibilitySlotSchema = z.object({
  level: hrResponsibilityLevelSchema,
  decides: z.boolean(),
  informed: z.boolean(),
  suspended_until: z.string().nullable(),
});
export type HrResponsibilitySlot = z.infer<typeof hrResponsibilitySlotSchema>;

/** Input di una posizione (default: livello 1, decide, non informato, non sospeso). */
export const hrResponsibilitySlotInputSchema = z.object({
  level: hrResponsibilityLevelSchema.default(1),
  decides: z.boolean().default(true),
  informed: z.boolean().default(false),
  suspended_until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
}).refine((v) => v.decides || v.informed, { message: 'Un responsabile deve almeno decidere o essere informato' });
export type HrResponsibilitySlotInput = z.infer<typeof hrResponsibilitySlotInputSchema>;

/**
 * Chiavi stabili dei settori seminati per tenant (rinominabili, disattivabili;
 * la chiave non cambia). Il POS trasferimenti cerca `warehouse`, non il nome.
 */
export const hrDepartmentSystemKeySchema = z.enum([
  'sales',
  'marketing',
  'customer_care',
  'warehouse',
  'field_tech',
  'admin',
  'hr',
  'management',
]);
export type HrDepartmentSystemKey = z.infer<typeof hrDepartmentSystemKeySchema>;

/** Riga hr_departments — catalogo settori del tenant. */
export const hrDepartmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  is_active: z.boolean(),
  /** Chiave stabile per i settori seminati; null per i settori creati dal tenant. */
  system_key: z.string().nullable(),
});
export type HrDepartment = z.infer<typeof hrDepartmentSchema>;

/** Stato recinto sintetico di un utente (per badge organigramma). */
export const hrScopeStateSchema = z.enum(['all', 'restricted', 'none']);
export type HrScopeState = z.infer<typeof hrScopeStateSchema>;

/** Titolare di una posizione di responsabilità (livello + modalità) di un settore. */
export const hrDepartmentResponsibleSchema = hrResponsibilitySlotSchema.extend({
  user_id: z.string(),
  user_name: z.string(),
  role_name: z.string().nullable(),
  is_active: z.boolean(),
  /** roles.is_manager oppure ruolo admin: eleggibilità a responsabile. */
  is_manager_eligible: z.boolean(),
  /** Detiene hr.requests.approve (o è privilegiato): gap se false. */
  has_approval_permission: z.boolean(),
  scope_state: hrScopeStateSchema,
  /** Recinto leggibile per il badge (es. "Milano, Torino" / "Azienda Nord"); null se all/none. */
  scope_label: z.string().nullable(),
});
export type HrDepartmentResponsible = z.infer<typeof hrDepartmentResponsibleSchema>;

/** Risorsa coperta (membership) mostrata sotto un settore — view-only, derivata. */
export const hrOrgChartMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  role_name: z.string().nullable(),
  is_active: z.boolean(),
  /** Task #4257 — badge sola lettura "concessioni: N" (concessioni vive). */
  active_grants_count: z.number(),
});
export type HrOrgChartMember = z.infer<typeof hrOrgChartMemberSchema>;

/** Utente eleggibile responsabile (per i picker slot/responsabilità). */
/** Badge già posseduto da un manager abbinabile (per "badge <Settore> già in altre N sedi"). */
export const hrEligibleManagerBadgeSchema = hrResponsibilitySlotSchema.extend({
  department_id: z.string(),
  /** Sedi coperte dal recinto del manager (dove quel badge vale). */
  covered_store_count: z.number(),
});
export type HrEligibleManagerBadge = z.infer<typeof hrEligibleManagerBadgeSchema>;

/** Stato del bundle permessi di un dominio per il manager SULLA sede del picker. */
export const hrEligibleManagerDomainPermissionsSchema = z.object({
  domain: hrRoutingDomainSchema,
  /** Etichette dei permessi del bundle non posseduti con portata sulla sede; vuoto = completi. */
  missing_labels: z.array(z.string()),
});
export type HrEligibleManagerDomainPermissions = z.infer<typeof hrEligibleManagerDomainPermissionsSchema>;

/** Manager attivo eleggibile il cui recinto copre la sede (picker per cella e "manager abbinabili"). */
export const hrEligibleManagerSchema = z.object({
  id: z.string(),
  name: z.string(),
  role_name: z.string().nullable(),
  is_active: z.boolean(),
  badges: z.array(hrEligibleManagerBadgeSchema),
  permissions_by_domain: z.array(hrEligibleManagerDomainPermissionsSchema),
});
export type HrEligibleManager = z.infer<typeof hrEligibleManagerSchema>;

// ============================================================================
// 8b) Organigramma a griglia (sedi × settori) — GET /hr/org-chart/grid.
//     Il manager è l'unico nodo: ruolo + settori di responsabilità + recinto
//     (risolto in sedi dallo scope guard). La squadra è la sede.
// ============================================================================

/** Perché un responsabile oggi non è disponibile (null = disponibile). */
export const hrOrgChartUnavailableReasonSchema = z.enum(['sospeso', 'assente']);
export type HrOrgChartUnavailableReason = z.infer<typeof hrOrgChartUnavailableReasonSchema>;

/** Responsabile che copre una cella (sede × settore) col proprio recinto. */
export const hrOrgChartGridResponsibleRefSchema = hrResponsibilitySlotSchema.extend({
  user_id: z.string(),
  user_name: z.string(),
  is_active: z.boolean(),
  is_manager_eligible: z.boolean(),
  has_approval_permission: z.boolean(),
  scope_state: hrScopeStateSchema,
  /** Fino a quando (YYYY-MM-DD, inclusa) è indisponibile oggi: sospensione manuale o assenza HR approvata in corso. */
  unavailable_until: z.string().nullable(),
  unavailable_reason: hrOrgChartUnavailableReasonSchema.nullable(),
});
export type HrOrgChartGridResponsibleRef = z.infer<typeof hrOrgChartGridResponsibleRefSchema>;

/** Un livello della cella: tutti i responsabili di quel livello che coprono la sede. */
export const hrOrgChartGridLevelSchema = z.object({
  level: hrResponsibilityLevelSchema,
  responsibles: z.array(hrOrgChartGridResponsibleRefSchema),
});
export type HrOrgChartGridLevel = z.infer<typeof hrOrgChartGridLevelSchema>;

export const hrOrgChartGridCellSchema = z.object({
  /** Pila livelli (crescente) con chi copre la sede; vuota = cella scoperta. */
  levels: z.array(hrOrgChartGridLevelSchema),
  /** Livello che riceverebbe una pratica creata OGGI (decisore eleggibile, con permesso, disponibile); null = nessuno. */
  reachable_level: hrResponsibilityLevelSchema.nullable(),
  /** Livello raggiungibile ignorando assenze e sospensioni; null = mai raggiungibile (scoperto strutturale). */
  structural_level: hrResponsibilityLevelSchema.nullable(),
  /** Conseguenza dello scoperto (solo se settore instradato senza livello raggiungibile), es. "le richieste HR arrivano agli admin". */
  gap_consequence: z.string().nullable(),
  /** Riserva: chi ha il permesso di approvazione del dominio con recinto sulla sede ma NESSUN badge (può agire, non riceve). */
  can_act_also: z.array(z.object({
    user_id: z.string(),
    user_name: z.string(),
    role_name: z.string().nullable(),
    is_manager_eligible: z.boolean(),
  })),
});
export type HrOrgChartGridCell = z.infer<typeof hrOrgChartGridCellSchema>;
export type HrOrgChartGridCanActUser = HrOrgChartGridCell['can_act_also'][number];

/** Semaforo della sede: ok = ogni settore instradato ha un livello raggiungibile. */
export const hrOrgChartStoreCoverageSchema = z.object({
  routed_departments: z.number(),
  covered_departments: z.number(),
  gaps: z.number(),
  state: z.enum(['ok', 'gaps', 'no_routed']),
});
export type HrOrgChartStoreCoverage = z.infer<typeof hrOrgChartStoreCoverageSchema>;

/** Risorsa della sede (user_stores) con l'etichetta settore self (sola reportistica). */
export const hrOrgChartGridMemberSchema = hrOrgChartMemberSchema.extend({
  department_id: z.string().nullable(),
  department_name: z.string().nullable(),
});
export type HrOrgChartGridMember = z.infer<typeof hrOrgChartGridMemberSchema>;

export const hrOrgChartGridStoreSchema = z.object({
  id: z.string(),
  name: z.string(),
  company_id: z.string().nullable(),
  is_active: z.boolean(),
  members: z.array(hrOrgChartGridMemberSchema),
  member_count: z.number(),
  coverage: hrOrgChartStoreCoverageSchema,
  /** Per settore instradato: chi copre la cella. Chiave = department_id. */
  cells: z.record(z.string(), hrOrgChartGridCellSchema),
});
export type HrOrgChartGridStore = z.infer<typeof hrOrgChartGridStoreSchema>;

export const hrOrgChartGridCompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  stores: z.array(hrOrgChartGridStoreSchema),
});
export type HrOrgChartGridCompany = z.infer<typeof hrOrgChartGridCompanySchema>;

/** Colonna della griglia: settore su cui arriva almeno una tipologia attiva (o con responsabili). */
export const hrOrgChartGridDepartmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  system_key: z.string().nullable(),
  is_active: z.boolean(),
  /** Tipologie attive che hanno questo settore in destinazione (dominio ∪ aggiuntivi). */
  request_type_count: z.number(),
  /** Domini di instradamento serviti dal settore (deriva il bundle permessi). */
  domains: z.array(hrRoutingDomainSchema),
});
export type HrOrgChartGridDepartment = z.infer<typeof hrOrgChartGridDepartmentSchema>;

/** Vista per responsabile: settori di cui risponde + sedi coperte dal recinto. */
export const hrOrgChartGridResponsibleSchema = z.object({
  user_id: z.string(),
  user_name: z.string(),
  role_name: z.string().nullable(),
  is_active: z.boolean(),
  is_manager_eligible: z.boolean(),
  has_approval_permission: z.boolean(),
  scope_state: hrScopeStateSchema,
  scope_label: z.string().nullable(),
  /** Sedi coperte dal recinto (tutte le sedi del tenant se scope all; vuoto se none). */
  store_ids: z.array(z.string()),
  responsibilities: z.array(hrResponsibilitySlotSchema.extend({
    department_id: z.string(),
    department_name: z.string(),
  })),
});
export type HrOrgChartGridResponsible = z.infer<typeof hrOrgChartGridResponsibleSchema>;

export const hrOrgChartGridAlarmKindSchema = z.enum([
  /** Cella sede×settore instradata senza alcun decisore che la copra. */
  'cell_uncovered',
  /** Ci sono decisori ma nessun livello è raggiungibile (tutti non eleggibili o senza permesso). */
  'cell_unreachable',
  /** Il livello più basso non può agire (senza permesso / non eleggibile / richiedente): le pratiche saltano al livello N. */
  'cell_level_skipped',
  /** Livelli raggiungibili in teoria, ma oggi tutti assenti o sospesi: una pratica creata oggi finirebbe in fallback. */
  'cell_unavailable_today',
  /** Responsabile con badge ma recinto vuoto: allarme, non blocco. */
  'responsible_without_scope',
  /** Responsabile non più eleggibile (ruolo senza flag manager) o inattivo. */
  'responsible_not_eligible',
  /** Responsabile con badge ma senza il permesso di approvazione del dominio (riceve ma non può agire). */
  'responsible_without_permission',
  /** Risorsa attiva senza alcuna sede di lavoro (non appare in nessuna squadra). */
  'member_without_store',
]);
export type HrOrgChartGridAlarmKind = z.infer<typeof hrOrgChartGridAlarmKindSchema>;

/** Azione suggerita dall'allarme (la UI la rende come pulsante). */
export const hrOrgChartGridAlarmActionSchema = z.enum(['open_store', 'open_user', 'propose_grants']);
export type HrOrgChartGridAlarmAction = z.infer<typeof hrOrgChartGridAlarmActionSchema>;

export const hrOrgChartGridAlarmSchema = z.object({
  kind: hrOrgChartGridAlarmKindSchema,
  /** Testo con la conseguenza, mai il termine tecnico. */
  message: z.string(),
  department_id: z.string().nullable(),
  department_name: z.string().nullable(),
  store_id: z.string().nullable(),
  store_name: z.string().nullable(),
  user_id: z.string().nullable(),
  /** Solo per gli allarmi di cella: conseguenza dello scoperto (tooltip del chip). */
  consequence: z.string().nullable(),
  action: hrOrgChartGridAlarmActionSchema.nullable(),
});
export type HrOrgChartGridAlarm = z.infer<typeof hrOrgChartGridAlarmSchema>;

export const hrOrgChartGridSchema = z.object({
  summary: z.object({
    total_stores: z.number(),
    total_departments: z.number(),
    /** Celle instradate senza livello strutturalmente raggiungibile (scoperte + mai raggiungibili). */
    cells_uncovered: z.number(),
    /** Celle raggiungibili in teoria ma oggi senza decisori disponibili. */
    cells_unavailable_today: z.number(),
    total_responsibles: z.number(),
    total_members: z.number(),
    members_without_store: z.number(),
    alarms: z.number(),
  }),
  companies: z.array(hrOrgChartGridCompanySchema),
  /** Sedi senza ragione sociale (company_id null). */
  unassigned_stores: z.array(hrOrgChartGridStoreSchema),
  departments: z.array(hrOrgChartGridDepartmentSchema),
  responsibles: z.array(hrOrgChartGridResponsibleSchema),
  alarms: z.array(hrOrgChartGridAlarmSchema),
  /** Manager eleggibili il cui recinto copre la sede (picker per cella). Chiave = store_id. */
  eligible_by_store: z.record(z.string(), z.array(hrEligibleManagerSchema)),
  /** Risorse attive senza sede di lavoro. */
  members_without_store: z.array(hrOrgChartGridMemberSchema),
});
export type HrOrgChartGrid = z.infer<typeof hrOrgChartGridSchema>;

/** Riga del report "settore self ridotto a uno" (migrazione multi-settore → singolo). */
export const hrUserDepartmentMigrationRowSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user_name: z.string().nullable(),
  kept_department_id: z.string().nullable(),
  kept_department_name: z.string().nullable(),
  dropped_department_names: z.array(z.string()),
  created_at: wireTimestamp,
});
export type HrUserDepartmentMigrationRow = z.infer<typeof hrUserDepartmentMigrationRowSchema>;

/**
 * GET/PUT /hr/users/:userId/department — settore self della risorsa: UNICA
 * etichetta di reportistica (nullable). Non concorre mai all'instradamento.
 */
export const hrUserDepartmentSchema = z.object({
  department_id: z.string().nullable(),
  department_name: z.string().nullable(),
});
export type HrUserDepartment = z.infer<typeof hrUserDepartmentSchema>;

export const hrUserDepartmentUpdateInputSchema = z.object({
  department_id: z.string().uuid().nullable(),
});
export type HrUserDepartmentUpdateInput = z.infer<typeof hrUserDepartmentUpdateInputSchema>;

/** Una responsabilità di un utente (vista per-utente del modello per settore). */
export const hrUserResponsibilityItemSchema = hrResponsibilitySlotSchema.extend({
  department_id: z.string(),
});
export type HrUserResponsibilityItem = z.infer<typeof hrUserResponsibilityItemSchema>;

export const hrUserResponsibilitiesSchema = z.object({
  items: z.array(
    hrUserResponsibilityItemSchema.extend({
      department_name: z.string(),
    })
  ),
  /** Il ruolo attuale dell'utente è manager-eligible (per gating UI). */
  is_manager_eligible: z.boolean(),
});
export type HrUserResponsibilities = z.infer<typeof hrUserResponsibilitiesSchema>;

/**
 * Conflitto di recinto tra decisori di pari livello dello stesso settore
 * (409 SCOPE_OVERLAP). Due decisori dello stesso (settore, livello) sono
 * ammessi solo a recinti disgiunti (sedi + aziende espanse a sedi); `all` si
 * sovrappone a tutto; recinto non risolvibile (`none`) = rifiuto fail-closed.
 * Gli "informati" non partecipano al vincolo.
 */
export const hrScopeOverlapConflictSchema = z.object({
  department_id: z.string(),
  department_name: z.string(),
  level: hrResponsibilityLevelSchema,
  /** Utente che si sta insediando/modificando. */
  user_id: z.string(),
  user_name: z.string(),
  /** Pari-livello già in carica con cui il recinto si sovrappone. */
  other_user_id: z.string(),
  other_user_name: z.string(),
  /** overlap = sedi/aziende in comune; unresolvable = uno dei due recinti è vuoto/non risolvibile. */
  kind: z.enum(['overlap', 'unresolvable_scope']),
  shared_stores: z.array(z.object({ id: z.string(), name: z.string() })),
  shared_companies: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type HrScopeOverlapConflict = z.infer<typeof hrScopeOverlapConflictSchema>;

/** Payload 409 SCOPE_OVERLAP (route responsabilità, recinto utente, cambio azienda sede). */
export const hrScopeOverlapErrorPayloadSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  code: z.literal('SCOPE_OVERLAP'),
  conflicts: z.array(hrScopeOverlapConflictSchema),
});
export type HrScopeOverlapErrorPayload = z.infer<typeof hrScopeOverlapErrorPayloadSchema>;

// ============================================================================
// 8c) Task #4268 — riepilogo di assegnazione badge → permessi proposti.
// ============================================================================

/**
 * Recinto ATTUALE del responsabile, descritto per l'anteprima. I poteri creati
 * dall'assegnazione hanno sempre portata `base` (valgono dove vale il recinto
 * dell'utente, e lo seguono se cambia): qui NON c'è una portata di concessione
 * da salvare, solo la forma del recinto — tutte le sedi, una ragione sociale o
 * le sedi elencate.
 */
export const hrProposedGrantCoverageSchema = z.enum(['tenant', 'company', 'stores']);
export type HrProposedGrantCoverage = z.infer<typeof hrProposedGrantCoverageSchema>;
export const hrProposedGrantScopeSchema = z.object({
  coverage: hrProposedGrantCoverageSchema,
  company_id: z.string().nullable(),
  store_ids: z.array(z.string()),
  label: z.string(),
});
export type HrProposedGrantScope = z.infer<typeof hrProposedGrantScopeSchema>;

/** Un permesso del bundle: già posseduto con portata sufficiente oppure proposto. */
export const hrGrantProposalItemSchema = z.object({
  permission_id: z.string(),
  code: z.string(),
  label: z.string(),
  is_approval: z.boolean(),
  already_held: z.boolean(),
  /** Da dove viene il possesso: privilegiato, ruolo, concessione attiva; null se mancante. */
  held_via: z.enum(['privileged', 'role', 'grant']).nullable(),
});
export type HrGrantProposalItem = z.infer<typeof hrGrantProposalItemSchema>;

/** Livelli occupati nel settore da decisori con recinto sovrapposto al manager. */
export const hrAssignOccupiedLevelSchema = z.object({
  level: hrResponsibilityLevelSchema,
  user_names: z.array(z.string()),
});
export type HrAssignOccupiedLevel = z.infer<typeof hrAssignOccupiedLevelSchema>;

/** GET /hr/departments/:id/responsible/preview?user_id=… */
export const hrAssignResponsiblePreviewSchema = z.object({
  user_id: z.string(),
  user_name: z.string(),
  role_name: z.string().nullable(),
  is_manager_eligible: z.boolean(),
  department_id: z.string(),
  department_name: z.string(),
  domains: z.array(hrRoutingDomainSchema),
  /** Posizione attuale se già responsabile del settore, altrimenti null. */
  current_slot: hrResponsibilitySlotSchema.nullable(),
  /** Livelli già occupati nel settore da decisori il cui recinto si sovrappone a questo manager (per proporre il livello libero). */
  occupied_levels: z.array(hrAssignOccupiedLevelSchema),
  scope_state: hrScopeStateSchema,
  /** Sedi coperte dal recinto del manager (dove varrà il badge). */
  covered_stores: z.array(z.object({ id: z.string(), name: z.string(), company_name: z.string().nullable() })),
  /** Tipologie attive dei domini del settore che il manager riceverà. */
  active_request_type_count: z.number(),
  /** Portata su cui verrebbero create le concessioni; null se il recinto è vuoto. */
  proposed_scope: hrProposedGrantScopeSchema.nullable(),
  proposals: z.array(hrGrantProposalItemSchema),
  /** Motivo per cui non si possono proporre concessioni (recinto vuoto, attore senza potere), altrimenti null. */
  proposal_blocked_reason: z.string().nullable(),
});
export type HrAssignResponsiblePreview = z.infer<typeof hrAssignResponsiblePreviewSchema>;

/** Body POST /hr/departments/:id/responsible/assign — badge + concessioni spuntate, atomico. */
export const hrAssignResponsibleInputSchema = z.object({
  user_id: z.string().uuid(),
  slot: hrResponsibilitySlotInputSchema,
  /** Codici permesso del bundle da concedere (sottoinsieme delle proposte non già possedute). */
  grant_permission_codes: z.array(z.string().min(1)).max(20).default([]),
});
export type HrAssignResponsibleInput = z.infer<typeof hrAssignResponsibleInputSchema>;

export const hrAssignResponsibleResultSchema = z.object({
  warnings: z.array(z.string()),
  created_grants: z.array(permissionGrantSchema),
});
export type HrAssignResponsibleResult = z.infer<typeof hrAssignResponsibleResultSchema>;

// ============================================================================
// 8d) Task #4300 — assegnazione multi-settore atomica (un responsabile, N settori).
// Il singolo settore è il caso N=1 dello stesso contratto.
// ============================================================================

/** Anteprima per UN settore del riepilogo multi-settore. */
export const hrAssignResponsibleDepartmentPreviewSchema = z.object({
  department_id: z.string(),
  department_name: z.string(),
  is_active: z.boolean(),
  domains: z.array(hrRoutingDomainSchema),
  /** Posizione attuale se già responsabile del settore, altrimenti null. */
  current_slot: hrResponsibilitySlotSchema.nullable(),
  /** Conflitti di pari-rango: livelli dove il manager NON può decidere (con i nomi). */
  occupied_levels: z.array(hrAssignOccupiedLevelSchema),
  /** Livello proposto: quello attuale se già responsabile, altrimenti il primo libero. */
  proposed_level: hrResponsibilityLevelSchema,
  /** Tipologie attive che arrivano davvero al settore (stesso predicato della griglia). */
  active_request_type_count: z.number(),
  /** Permessi del bundle dei domini del settore (già posseduti o proposti). */
  proposals: z.array(hrGrantProposalItemSchema),
});
export type HrAssignResponsibleDepartmentPreview = z.infer<typeof hrAssignResponsibleDepartmentPreviewSchema>;

/** Body POST /hr/responsible-assignments/preview */
export const hrAssignResponsibleMultiPreviewInputSchema = z.object({
  user_id: z.string().uuid(),
  department_ids: z.array(z.string().uuid()).min(1).max(50),
});
export type HrAssignResponsibleMultiPreviewInput = z.infer<typeof hrAssignResponsibleMultiPreviewInputSchema>;

/** Risposta POST /hr/responsible-assignments/preview: parte comune al manager + una voce per settore. */
export const hrAssignResponsibleMultiPreviewSchema = z.object({
  user_id: z.string(),
  user_name: z.string(),
  role_name: z.string().nullable(),
  is_manager_eligible: z.boolean(),
  scope_state: hrScopeStateSchema,
  /** Sedi coperte dal recinto del manager (dove varranno i badge). */
  covered_stores: z.array(z.object({ id: z.string(), name: z.string(), company_name: z.string().nullable() })),
  /** Portata su cui verrebbero create le concessioni; null se il recinto è vuoto. */
  proposed_scope: hrProposedGrantScopeSchema.nullable(),
  /** Motivo per cui non si possono proporre concessioni (recinto vuoto, attore senza potere), altrimenti null. */
  proposal_blocked_reason: z.string().nullable(),
  /** Nell'ordine richiesto. */
  departments: z.array(hrAssignResponsibleDepartmentPreviewSchema),
  /** Unione dei permessi proposti, raggruppata per dominio (un codice compare in un solo dominio). */
  proposals_by_domain: z.array(z.object({
    domain: hrRoutingDomainSchema,
    /** Settori della richiesta che servono questo dominio. */
    department_ids: z.array(z.string()),
    proposals: z.array(hrGrantProposalItemSchema),
  })),
});
export type HrAssignResponsibleMultiPreview = z.infer<typeof hrAssignResponsibleMultiPreviewSchema>;

/** Body POST /hr/responsible-assignments — N badge + concessioni spuntate in UNA transazione. */
export const hrAssignResponsibleMultiInputSchema = z.object({
  user_id: z.string().uuid(),
  departments: z.array(z.object({
    department_id: z.string().uuid(),
    slot: hrResponsibilitySlotInputSchema,
  })).min(1).max(50),
  /** Codici permesso dell'unione dei bundle dei settori scelti (sottoinsieme delle proposte non già possedute). */
  grant_permission_codes: z.array(z.string().min(1)).max(40).default([]),
}).refine((v) => new Set(v.departments.map((d) => d.department_id)).size === v.departments.length, {
  message: 'Settore ripetuto nella richiesta',
  path: ['departments'],
});
export type HrAssignResponsibleMultiInput = z.infer<typeof hrAssignResponsibleMultiInputSchema>;

export const hrAssignResponsibleMultiResultSchema = hrAssignResponsibleResultSchema.extend({
  /** Settori scritti (nell'ordine richiesto). */
  assigned: z.array(z.object({ department_id: z.string(), department_name: z.string(), slot: hrResponsibilitySlotSchema })),
});
export type HrAssignResponsibleMultiResult = z.infer<typeof hrAssignResponsibleMultiResultSchema>;

/** Riga badge nella scheda utente: settore, sedi coperte, stato permessi del dominio. */
export const hrUserBadgeStatusSchema = hrResponsibilitySlotSchema.extend({
  department_id: z.string(),
  department_name: z.string(),
  domains: z.array(hrRoutingDomainSchema),
  covered_store_names: z.array(z.string()),
  scope_state: hrScopeStateSchema,
  missing_permissions: z.array(hrGrantProposalItemSchema),
  held_permissions: z.array(hrGrantProposalItemSchema),
});
export type HrUserBadgeStatus = z.infer<typeof hrUserBadgeStatusSchema>;

/** GET /hr/users/:userId/badges — badge dell'utente + riserva (permesso di dominio senza badge). */
export const hrUserBadgesSchema = z.object({
  badges: z.array(hrUserBadgeStatusSchema),
  /** Domini per cui l'utente ha il permesso di approvazione ma NESSUN badge (può agire, non riceve). */
  reserve_domains: z.array(hrRoutingDomainSchema),
  is_manager_eligible: z.boolean(),
});
export type HrUserBadges = z.infer<typeof hrUserBadgesSchema>;

/**
 * Body PATCH /hr/request-types/:id — destinazione multi-settore. Il server
 * rifiuta (400 ROUTING_DESTINATION_EMPTY) una destinazione risultante vuota su
 * tipologia attiva.
 */
export const hrRequestTypeUpdateInputSchema = z.object({
  label: z.string().min(1).max(255).optional(),
  follows_domain: z.boolean().optional(),
  /** Replace integrale dei settori aggiuntivi con il loro recapito. */
  additional_departments: z.array(hrRoutingAdditionalDepartmentInputSchema).optional(),
  approval_permission: z.string().min(1).optional(),
  is_active: z.boolean().optional(),
  sort_order: z.number().int().optional(),
  /**
   * Task #4302 — se presente, la modifica riguarda la FASE indicata della
   * tipologia flusso (follows_domain / additional_departments / is_active
   * della fase); gli altri campi non sono ammessi. Rifiutato su tipologie
   * non a fasi.
   */
  phase: hrRoutingFlowPhaseSchema.optional(),
});
export type HrRequestTypeUpdateInput = z.infer<typeof hrRequestTypeUpdateInputSchema>;

export const hrRequestTypeCreateInputSchema = z.object({
  // Max 30: hr_requests.type (varchar(30)) denormalizza il codice della tipologia.
  code: z.string().min(1).max(30).regex(/^[a-z0-9_]+$/),
  label: z.string().min(1).max(255),
  category: hrRequestTypeCategorySchema,
  domain: hrRoutingDomainSchema.default('hr'),
  unit: hrRequestTypeUnitSchema.default('day'),
  follows_domain: z.boolean().default(true),
  additional_departments: z.array(hrRoutingAdditionalDepartmentInputSchema).default([]),
  approval_permission: z.string().min(1).default('hr.requests.approve'),
  paid: z.boolean().default(true),
  requires_attachment: z.boolean().default(false),
  blocks_shift: z.boolean().default(true),
  blocks_availability: z.boolean().default(true),
  acknowledgment_only: z.boolean().default(false),
  sort_order: z.number().int().default(0),
});
export type HrRequestTypeCreateInput = z.infer<typeof hrRequestTypeCreateInputSchema>;

/** Riga del report di migrazione responsabilità (Task #3898). */
export const hrResponsibilityMigrationRowSchema = z.object({
  id: z.string(),
  department_id: z.string().nullable(),
  department_name: z.string().nullable(),
  manager_user_id: z.string().nullable(),
  manager_name: z.string().nullable(),
  resource_user_id: z.string().nullable(),
  resource_name: z.string().nullable(),
  source_rank: z.string().nullable(),
  action: z.enum(['assigned', 'deduplicated', 'demoted', 'discarded', 'general_skipped']),
  reason: z.string(),
  created_at: z.string(),
});
export type HrResponsibilityMigrationRow = z.infer<typeof hrResponsibilityMigrationRowSchema>;

// ============================================================================
// 9) Impostazioni HR — GET/PUT /hr/settings
// ============================================================================

export const hrSettingsSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    geofencing_enabled: z.boolean(),
    anomaly_late_threshold_minutes: z.number(),
    anomaly_early_leave_threshold_minutes: z.number(),
    anomaly_overtime_threshold_minutes: z.number(),
    coverage_warning_threshold: z.number(),
    coverage_critical_threshold: z.number(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
  })
  .passthrough();
export type HrSettings = z.infer<typeof hrSettingsSchema>;

// ============================================================================
// 10) Dashboard HR — stats, comparison, anomalie unificate, in turno
// ============================================================================

export const hrDashboardStatsSchema = z.object({
  total_users: z.number(),
  present_today: z.number(),
  active_now: z.number(),
  late_today: z.number(),
  absent_today: z.number(),
  pending_requests: z.number(),
});
export type HrDashboardStats = z.infer<typeof hrDashboardStatsSchema>;

export const hrDashboardComparisonDaySchema = z.object({
  date: wireDate,
  planned_count: z.number(),
  planned_hours: wireNumeric,
  actual_count: z.number(),
  actual_hours: wireNumeric,
});
export type HrDashboardComparisonDay = z.infer<typeof hrDashboardComparisonDaySchema>;

/** Anomalia timbrature (famiglia attendance) — shape storica + family. */
export const hrAttendanceAnomalySchema = z
  .object({
    family: z.literal('attendance'),
    type: z.enum(['missing_clockin', 'late', 'out_of_shift']),
    user_id: z.string().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    date: wireDate.optional(),
    store_name: z.string().nullable().optional(),
    late_minutes: z.number().optional(),
  })
  .passthrough();
export type HrAttendanceAnomaly = z.infer<typeof hrAttendanceAnomalySchema>;

/** Anomalia copertura (famiglia coverage) — §7.3. */
export const hrCoverageAnomalyItemSchema = z.object({
  family: z.literal('coverage'),
  type: hrCoverageAnomalyTypeSchema,
  severity: z.enum(['info', 'warning', 'critical']),
  store_id: z.string(),
  store_name: z.string(),
  date: wireDate,
  time_from: z.string().nullable(),
  time_to: z.string().nullable(),
  required_staff: z.number().nullable(),
  planned_staff: z.number().nullable(),
  coverage_pct: z.number().nullable(),
  message: z.string(),
});
export type HrCoverageAnomalyItem = z.infer<typeof hrCoverageAnomalyItemSchema>;

export const hrDashboardAnomalySchema = z.discriminatedUnion('family', [
  hrAttendanceAnomalySchema,
  hrCoverageAnomalyItemSchema,
]);
export type HrDashboardAnomaly = z.infer<typeof hrDashboardAnomalySchema>;

export const hrDashboardAnomaliesResponseSchema = z.object({
  anomalies: z.array(hrDashboardAnomalySchema),
  settings: hrSettingsSchema.nullable().optional(),
});
export type HrDashboardAnomaliesResponse = z.infer<typeof hrDashboardAnomaliesResponseSchema>;

export const hrDashboardEmployeeDetailSchema = z
  .object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    days_worked: z.number(),
    planned_hours: wireNumeric,
    actual_hours: wireNumeric,
    delta_hours: wireNumeric,
    late_count: z.number(),
    out_of_shift_count: z.number(),
  })
  .passthrough();
export type HrDashboardEmployeeDetail = z.infer<typeof hrDashboardEmployeeDetailSchema>;

/** Pannello "in turno oggi/prossimi giorni" — da assignments ∩ attendance live. */
export const hrOnShiftEntrySchema = z.object({
  user_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  store_id: z.string(),
  store_name: z.string(),
  date: wireDate,
  template_name: z.string(),
  template_start_time: z.string(),
  template_end_time: z.string(),
  template_color: z.string().nullable(),
  status: z.string(),
  /** clocked | expected | late — incrocio con attendance del giorno. */
  live_status: z.enum(['clocked', 'expected', 'late', 'completed']),
});
export type HrOnShiftEntry = z.infer<typeof hrOnShiftEntrySchema>;

// ============================================================================
// 11) Timbrature — riga attendance con i nuovi campi detection
// ============================================================================

export const hrAttendanceRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    user_id: z.string(),
    shift_assignment_id: z.string().nullable(),
    store_id: z.string(),
    date: hrCalendarDateSchema,
    clock_in: wireTimestamp,
    clock_out: wireTimestamp.nullable(),
    break_start: wireTimestamp.nullable(),
    break_end: wireTimestamp.nullable(),
    total_work_minutes: z.number().nullable(),
    total_break_minutes: z.number().nullable(),
    status: z.string(),
    clock_in_method: z.string(),
    is_out_of_shift: z.boolean(),
    late_minutes: z.number().nullable(),
    early_leave_minutes: z.number().nullable(),
    overtime_minutes: z.number().nullable(),
    out_of_shift_reason: z.string().nullable(),
    qr_store_mismatch: z.boolean(),
    notes: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    // alias
    store_name: z.string().nullable().optional(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string().optional(),
    shift_name: z.string().nullable().optional(),
    shift_start: z.string().nullable().optional(),
    shift_end: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    distance_meters: z.number().nullable().optional(),
  })
  .passthrough();
export type HrAttendanceRow = z.infer<typeof hrAttendanceRowSchema>;

export const hrCurrentStatusSchema = z.object({
  attendance: hrAttendanceRowSchema.nullable(),
  shift: z.object({
    id: z.string(),
    date: hrCalendarDateSchema,
    shift_name: z.string(),
    start_time: z.string(),
    end_time: z.string(),
    store_id: z.string(),
    store_name: z.string().nullable(),
    color: z.string().nullable(),
    breaks: z.array(hrShiftTemplateBreakSchema),
  }).nullable(),
  user_stores: z.array(z.object({ store_id: z.string(), store_name: z.string() })),
});
export type HrCurrentStatus = z.infer<typeof hrCurrentStatusSchema>;
